import { useEffect } from 'react';
import { Outlet } from 'react-router-dom';
import { useUser } from '@/queries/useUser';
import { configSettings } from '@/lib/config';
import { useExperiments } from '@/features/Experiments/hooks';
import { EXPERIMENTS } from '@/features/Experiments/constants';

/**
 * Route-level guard for the V2 funnels experience.
 *
 * Only users with the `FUNNELS_V2` experiment enabled are allowed to render
 * the React v2 funnels UI. Everyone else is redirected to the legacy Vue admin,
 * where v1 funnels still live. This is the mirror of Vue's `beforeEach`, which
 * pushes v2-flagged users in the other direction.
 */
export const FunnelsBetaGuard = () => {
    const { user, isUserLoading } = useUser();
    const { hasExperiment } = useExperiments();

    const hasV2Access = hasExperiment(EXPERIMENTS.FUNNELS_V2);

    useEffect(() => {
        // Wait until we actually have a loaded user before making a decision.
        if (isUserLoading || !user) return;
        if (hasV2Access) return;

        // No v2 access → bounce to legacy Vue admin.
        // `legacyAdminDomain` is already suffixed with '/' in config.
        const legacyDomain = configSettings.legacyAdminDomain?.replace(/\/+$/, '');
        if (!legacyDomain) return;

        const currentPath = window.location.pathname + window.location.search;
        window.location.href = `${legacyDomain}${currentPath}`;
    }, [hasV2Access, isUserLoading, user]);

    // Don't render the v2 UI until we've confirmed access. Showing it briefly
    // before the redirect would flash content at v1 users.
    if (isUserLoading || !user || !hasV2Access) {
        return null;
    }

    return <Outlet />;
};
